import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _, { isFunction } from 'lodash';
import AutoCompleteSelect from '../AutoCompleteSelect';
import { AddIcon, CloseIcon } from '../Icon';
import { classNames, onKeyUpEnterOrSpace } from '../../utils';
import { equipmentAPI } from '../../apis';
import { Counter } from '../LongLoanReminder';
import { i18n } from '../../language';
import { EQUIPMENT_STATUS_ENUM } from '../../comm/constant';
import Text from '../Text';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
  },
  boldTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: '#1973e6',
  },
  wardAutoSelect: {
    marginTop: '1rem',
  },
  categoryTab: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    border: 'solid 1px #538fff',
    borderRadius: '22px',
    backgroundColor: '#f5faff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#1973e6',
    gridColumnGap: '0.5rem',
  },
  emptyCategoryTab: {
    borderRadius: '50%',
    width: '2.75rem',
    height: '2.75rem',
    minWidth: '2.75rem',
    minHeight: '2.75rem',
    justifyContent: 'center',
  },
  addButton: {
    width: '2.75rem',
    height: '2.75rem',
    borderRadius: '50%',
    border: 'solid 1px #538fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addIcon: {
    fontSize: '1.125rem',
    color: '#538fff',
  },
  categoryTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#c0c6cb',
  },
  categoryTable: {
    '&>div': {
      marginBottom: '1.8rem',
    },
    '&>div:last-child': {
      margin: '0',
    },
  },
  categoryTableText: {
    fontSize: '1rem',
    color: '#2e4469',
    fontFamily: 'NotoSans-Medium',
  },
  closeIcon: {
    fontSize: '1rem',
    color: '#538fff',
  },
  star: {
    color: '#ff0300',
  },
  removeIcon: {
    fontSize: '1.5rem',
    color: '#fff',
    borderRadius: '50%',
    backgroundColor: '#538fff',
    padding: '0.2rem',
  },
  removeButton: {
    zIndex: '9999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const EquipmentAsset = ({
  data,
  flag,
  setFlag,
  changeDataHandler,
  provider,
  isFloorProvider = true,
  provideCategorys = [],
  controller,
  modelB = false,
  newRequest = false,
  status,
  setSelectedCategoryWards,
  categorySatelliteStoresId = null,
}) => {
  const classes = useStyles();
  const loginInfo = useSelector((state) => state.loginInfo);
  const [categoryList, setCategoryList] = useState();
  const showData = data[flag];
  const radioCategorys = useMemo(() => {
    if (provider && categoryList) {
      return _.filter(categoryList, (item) =>
        _.some(item.wards, (wardsItem) => wardsItem.wardId === provider && wardsItem.equipmentCount === 1)
      );
    }
    return [];
  }, [provider, categoryList]);

  // 获取选中的设备对应的wards
  useEffect(() => {
    if (_.isArray(categoryList) && _.isArray(data)) {
      // 获取
      let selectedCategoryWards = _.filter(categoryList, (item) =>
        _.some(data, (categoryItem) => String(categoryItem.category) === String(item.categoryId))
      );
      // 过滤provider以外的ward
      selectedCategoryWards = _.map(selectedCategoryWards, (item) =>
        _.find(item.wards, (ward) => String(ward.wardId) === String(provider))
      );

      setSelectedCategoryWards(selectedCategoryWards);
    }
  }, [data, categoryList, provider]);

  // 獲取 available qty
  const getAvailableQty = useCallback(async () => {
    try {
      // 如果當前tab還沒選擇category，則中斷
      if (!showData.category) {
        return;
      }

      const { result } = await equipmentAPI.getAvailableQty({
        provideWardId: provider,
        categoryId: showData.category,
        satelliteStoresId: categorySatelliteStoresId,
      });
      changeDataHandler({ type: 'updateAvailableQty', flag })(result);
    } catch (error) {
      console.log(error);
    }
  }, [provider, showData.category, flag, changeDataHandler, categorySatelliteStoresId]);

  useEffect(() => {
    (async () => {
      await getAvailableQty();
    })();
  }, [getAvailableQty]);

  // 獲取 category 列表
  const getCategory = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getCategoryList({ hospitalId: loginInfo.hospitalId, wardId: null });

      setCategoryList(result.categories);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  useEffect(() => {
    (async () => {
      await getCategory();
    })();
  }, []);

  // 所有 category
  const allCategory = useMemo(() => {
    let all = [];
    if (_.isArray(categoryList)) {
      // 所有 category
      all = _.map(categoryList, (item) => ({
        text: item.categoryName,
        value: item.categoryId,
        wardIds: _.map(item.wards, ({ wardId }) => wardId),
      }));
    }

    if (_.isArray(provideCategorys) && provideCategorys.length > 0) {
      // 根据当前Provider过滤category
      all = _.filter(all, (item) => _.some(provideCategorys, (categoryItem) => categoryItem === item.value));
    }

    // 如果是new並且有值
    if (data[0].category !== '' && newRequest) {
      if (_.some(data, (item) => _.some(radioCategorys, (radioItem) => radioItem.categoryId === item.category))) {
        // 只有單個選項的category
        all = _.filter(all, (item) => _.some(data, (dataItem) => dataItem.category === item.value));
      } else {
        // 可以有多個選項的category
        all = _.filter(all, (item) => !_.some(radioCategorys, (radioItem) => radioItem.categoryId === item.value));
      }
    }

    return all;
  }, [categoryList, provideCategorys, radioCategorys, data, newRequest]);

  // 下拉框選項
  const itemList = useMemo(() => {
    let categorys = [];

    if (_.isArray(categoryList)) {
      categorys = _.map(categoryList, (item) => ({
        text: item.categoryName,
        value: item.categoryId,
        wardIds: _.map(item.wards, ({ wardId }) => wardId),
      }));
    }

    if (_.isArray(provideCategorys) && provideCategorys.length > 0) {
      categorys = _.filter(categorys, (item) =>
        _.some(provideCategorys, (categoryItem) => categoryItem === item.value)
      );
    }

    categorys = _.filter(
      categorys,
      (item) => !_.some(data, (dataItem) => dataItem.category === item.value) || item.value === showData.category
    );

    // 如果是new request
    if (newRequest) {
      if (
        radioCategorys.length > 0 &&
        _.some(data, (item) => _.some(radioCategorys, (radioItem) => radioItem.categoryId === item.category))
      ) {
        // 只有單個選項的category
        categorys = _.filter(categorys, (categoryItem) =>
          _.some(radioCategorys, (radioItem) => radioItem.categoryId === categoryItem.value)
        );
      } else if (
        !_.some(data, (item) => _.some(radioCategorys, (radioItem) => radioItem.categoryId === item.category)) &&
        data[0].category !== ''
      ) {
        // 可以有多個選項的category
        categorys = _.filter(
          categorys,
          (categoryItem) => !_.some(radioCategorys, (radioItem) => radioItem.categoryId === categoryItem.value)
        );
      }
    }

    return categorys;
  }, [categoryList, data, provideCategorys, showData, radioCategorys, data, newRequest]);

  // tab 的點擊事件
  const categoryTabHandler = useCallback(
    (props) => () => {
      setFlag(props);
    },
    []
  );

  // 添加 category tab
  const addCategoryHandler = useCallback(() => {
    if (isFunction(changeDataHandler)) {
      if (_.some(data, (item) => item.category === '')) {
        return;
      }
      changeDataHandler({ type: 'addEquipment' });
      setFlag(data.length);
    }
  }, [changeDataHandler, data]);

  // 選擇 category
  const changeCategoryHandler = useCallback(
    (value) => {
      if (isFunction(changeDataHandler)) {
        if (_.some(data, (item) => item.category === value)) {
          // 去重
          return;
        }

        let categoryName = _.find(itemList, (item) => item.value === value).text;
        let wardIds = _.find(itemList, (item) => item.value === value).wardIds;

        changeDataHandler({ type: 'changeCategory', flag })(value, categoryName, wardIds);
      }
    },
    [changeDataHandler, flag, itemList]
  );

  // 刪除 asset
  const removeAssetHandler = useCallback(
    (id) => () => {
      if (isFunction(changeDataHandler)) {
        changeDataHandler({ type: 'removeAsset', flag })(id);
      }
    },
    [flag, changeDataHandler]
  );

  // 刪除 category
  const removeCategoryHandler = useCallback(
    (index) => (event) => {
      if (isFunction(changeDataHandler)) {
        event.stopPropagation();
        changeDataHandler({ type: 'removeCategory' })(index);
        if (flag === 0) {
          return;
        } else if (flag === data.length - 1) {
          setFlag(flag - 1);
        }
      }
    },
    [flag, changeDataHandler, data]
  );

  // 處理 loan Qty
  const loanQtyHandler = useCallback(
    (value) => {
      if (isFunction(changeDataHandler)) {
        return changeDataHandler({ type: 'updateLoanQty', flag })(value);
      }
    },
    [flag, changeDataHandler]
  );

  const radioMemo = useMemo(
    () => _.some(radioCategorys, (item) => item.categoryId === showData.category) && newRequest,
    [radioCategorys, showData.category, newRequest]
  );

  useEffect(() => {
    if (radioMemo) {
      loanQtyHandler(1);
    }
  }, [radioMemo, loanQtyHandler]);

  const qtyCtrl = useMemo(() => {
    if (radioMemo) {
      // 如果只有單個選項，就關閉， 只在new request的時候
      return false;
    } else return controller.loanQty;
  }, [controller.loanQty, radioMemo]);

  const categoryCtrl = useMemo(() => {
    if (showData.category !== '') {
      return true;
    } else {
      return !(showData.assets.length === 0 && controller.category);
    }
  }, [showData.assets.length, controller.category, showData.category]);

  const showAddEquipmentBtn = useMemo(() => {
    // 如果当前所选provider不是楼层就不能再加
    return isFloorProvider && controller.category && data.length !== allCategory.length;
  }, [controller, allCategory, data, isFloorProvider]);

  return (
    <>
      <Box display="flex" alignItems="center" mb="0.75rem" flexWrap="wrap" gridGap="0.75rem">
        {data.map((item, index) => (
          <ButtonBase
            id={`equipment-category-tab-${index}`}
            className={item?.category ? classes.categoryTab : classNames(classes.categoryTab, classes.emptyCategoryTab)}
            style={{ backgroundColor: index !== flag && '#fff' }}
            onClick={categoryTabHandler(index)}
            key={item.category}
          >
            <span>{`#${index + 1}`}</span>
            {item?.category && (
              <>
                <span>{_.find(allCategory, (categoryItem) => categoryItem.value === item?.category)?.text}</span>
                {controller.category && (
                  <div
                    id={`equipment-category-tab-remove-${index}`}
                    className={classes.removeButton}
                    onClick={removeCategoryHandler(index)}
                    tabIndex={0}
                    onKeyUp={(e) => onKeyUpEnterOrSpace(e, removeCategoryHandler(index))}
                  >
                    <CloseIcon className={classes.removeIcon} />
                  </div>
                )}
              </>
            )}
          </ButtonBase>
        ))}
        {showAddEquipmentBtn && (
          <ButtonBase id="equipment-category-tab-add" className={classes.addButton} onClick={addCategoryHandler}>
            <AddIcon className={classes.addIcon} />
          </ButtonBase>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        p="0.875rem 1.25rem 1.5rem"
        border="solid 1px #538fff"
        borderRadius="8px"
        bgcolor="#f5faff"
        mb="1.25rem"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          pb={modelB && showData.assets.length === 0 ? '0' : '1.813rem'}
          borderBottom={modelB && showData.assets.length === 0 ? 'unset' : 'solid 1px #e6e7e8'}
        >
          <Box display="flex" flexDirection="column" flexBasis="73%">
            <Box display="flex" justifyContent="space-between">
              <div className={classes.title}>
                {i18n.t('EQUIPMENT_VIEWREQUEST_EQUIPMENT_CATEGORY')} <span className={classes.star}>*</span>
              </div>
              <div className={classes.boldTitle}>{`${i18n.t('EQUIPMENT_VIEWREQUEST_AVAILABLE_QTY')}: ${
                showData.availableQty
              }`}</div>
            </Box>
            <AutoCompleteSelect
              className={classes.wardAutoSelect}
              id="ViewRequest_autoSelect-category"
              width="100%"
              height="3.5rem"
              itemList={itemList}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_EQUIPMENT_CATEGORY')}
              value={showData.category}
              onChange={changeCategoryHandler}
              disabled={categoryCtrl}
            />
          </Box>
          <Box display="flex" flexDirection="column" flexBasis="23.6%" justifyContent="space-between">
            <div className={classes.title}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_LOAN_QTY')} <span className={classes.star}>*</span>
            </div>
            <Counter value={showData.loanQty} onChange={loanQtyHandler} ctrl={qtyCtrl} minNum={1} />
          </Box>
        </Box>
        {modelB && showData.assets.length === 0 ? null : (
          <>
            <Box display="flex" p="1.5rem 1.5rem 0.563rem">
              <Box className={classes.categoryTitle} display="flex" flexBasis="15%" mr="1rem">
                {i18n.t('EQUIPMENT_VIEWREQUEST_ASSET_ID')}
              </Box>
              <Box className={classes.categoryTitle} display="flex" flexBasis="26.5%" mr="1rem">
                {i18n.t('EQUIPMENT_VIEWREQUEST_REF_NO')}
              </Box>
              <Box className={classes.categoryTitle} display="flex" flexBasis="24%" mr="1rem">
                {i18n.t('EQUIPMENT_VIEWREQUEST_MODEL')}
              </Box>
              {status !== 4 && (
                <Box className={classes.categoryTitle} display="flex" flexBasis="34%" mr="1rem">
                  {i18n.t('EQUIPMENT_STATUS_T')}
                </Box>
              )}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              border="solid 1px #e4e7eb"
              borderRadius="12px"
              bgcolor={controller.asset ? '#fff' : '#f5f5f5'}
              p="1.25rem 1.5rem"
              className={classes.categoryTable}
            >
              {showData.assets.length > 0 ? (
                showData.assets.map((item, index) => (
                  <Box key={item.assetId}>
                    <Box display="flex" width="100%" mb="0.8rem" style={{ position: 'relative' }}>
                      <Box className={classes.categoryTableText} display="flex" flexBasis="15%" mr="1rem">
                        {item.assetId}
                      </Box>
                      <Box className={classes.categoryTableText} display="flex" flexBasis="26.5%" mr="1rem">
                        {item.localCode}
                      </Box>
                      <Box className={classes.categoryTableText} display="flex" flexBasis="24%" mr="1rem">
                        {item.model}
                      </Box>
                      {status !== 4 && (
                        <Box className={classes.categoryTableText} display="flex" flexBasis="34%" mr="1rem">
                          {i18n.t(
                            _.find(EQUIPMENT_STATUS_ENUM, (i) =>
                              i.enumKey === item.eqStatus ? item.eqStatus : item.status
                            )?.textKey ?? 'EQUIPMENT_VIEWREQUEST_BLANK'
                          )}
                        </Box>
                      )}
                      <Box style={{ position: 'absolute', right: '0', zIndex: '1' }}>
                        {controller.asset && (
                          <ButtonBase id={`equipment-asset-remove-${index}`} onClick={removeAssetHandler(item.assetId)}>
                            <CloseIcon className={classes.closeIcon} />
                          </ButtonBase>
                        )}
                      </Box>
                    </Box>
                    {item.remark && (
                      <Box>
                        <Text fontSize="1rem" mb="0.8rem" color="#C0C6CB">
                          Equipment Remarks
                        </Text>
                        <Box
                          p="0.62rem 1.25rem"
                          border="1px solid #C0C6CB"
                          borderRadius="0.625rem 0.625rem 0.625rem 0.625rem"
                          bgcolor={controller.asset ? '#f5f5f5' : ''}
                        >
                          <Text color="#3E3E3E" fontSize="1rem" style={{ whiteSpace: 'pre-line' }}>
                            {item.remark}
                          </Text>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Box display="flex" width="100%" mb="1rem">
                  <Box className={classes.categoryTableText} display="flex" flexBasis="15%" mr="1rem">
                    -
                  </Box>
                  <Box className={classes.categoryTableText} display="flex" flexBasis="26.5%" mr="1rem">
                    -
                  </Box>
                  <Box className={classes.categoryTableText} display="flex" flexBasis="24%" mr="1rem">
                    -
                  </Box>
                  <Box className={classes.categoryTableText} display="flex" flexBasis="33%" mr="1rem">
                    -
                  </Box>
                  <Box style={{ position: 'absolute', right: '0', zIndex: '1' }} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default EquipmentAsset;
